import {
  CalendarDays, Camera, ClipboardList,
  DollarSign, Home, LayoutDashboard, Megaphone, Trophy, Users,
  Database, Dumbbell, Crown, MessageSquare, Receipt,
  Landmark, ShieldCheck, Gavel, Wallet, BookOpen, Rocket
} from "lucide-react";
import { rolePermissions } from "./data";

// Phase 3 — Grouped nav. Each item now declares a `group` so the sidebar
// can render them under collapsible section headers ("My Stuff", "Team",
// "Club", "Admin"). Groups + their order are defined below in NAV_GROUPS.
// The flat `nav` array (legacy consumers) is preserved in source order.
export const nav = [
  // MY STUFF
  { key: "dashboard",     label: "Dashboard",      icon: LayoutDashboard, group: "my" },
  { key: "messages",      label: "Messages",       icon: MessageSquare,   group: "my" },
  // Phase 4 — Splitwise-style personal shared expenses. Access is scoped
  // in the backend to payer + participants; no admin bulk view.
  { key: "splits",        label: "Splits",         icon: Receipt,         group: "my" },
  // Notifications removed from sidebar nav — surfaced via the top-left
  // bell icon in each shell's topbar. Module.jsx still routes the
  // `notifications` key in case anything deep-links to it.

  // TEAM
  { key: "schedule",      label: "Schedules",      icon: CalendarDays,    group: "team" },
  // Availability is a cross-match matrix for captains to plan lineups.
  // It's reached from a tab on the Schedules page (see Schedules.jsx),
  // not from the sidebar — regular players don't need a second RSVP
  // surface next to the one already on Schedule cards.
  { key: "squad",         label: "Squads",         icon: ClipboardList,   group: "team" },
  // Practice + Rankings are Rowdys-only features. `requiresTeam` gets
  // checked in both shells against state.teams membership so a non-Rowdys
  // player never sees these entries, regardless of role.
  { key: "practice",      label: "Practice",       icon: Dumbbell,        group: "team",
    requiresTeam: "ROWDYS_CRICKET" },
  { key: "rankings",      label: "Rankings",       icon: Crown,           group: "team",
    requiresTeam: "ROWDYS_CRICKET" },
  // Tournaments — scoped bundles of teams + fixtures + standings + splits.
  // v1 ships volleyball only; cricket folds in later.
  { key: "tournaments",   label: "Tournaments",    icon: Trophy,          group: "team" },

  // CLUB
  { key: "members",       label: "Members",        icon: Users,           group: "club" },
  { key: "groups",        label: "Groups",         icon: Home,            group: "club" },
  { key: "teams",         label: "Teams",          icon: Trophy,          group: "club" },
  { key: "announcements", label: "Announcements",  icon: Megaphone,       group: "club" },
  { key: "media",         label: "Gallery",        icon: Camera,          group: "club" },
  // League entries are added below as their own section (see
  // DESKTOP_SECTIONS "league") after the merge of the League/Umpire
  // workspaces into Gabruz Universe.

  // ADMIN
  { key: "finance",       label: "Finance",        icon: DollarSign,      group: "admin" },
  { key: "admin",         label: "Admin",          icon: Database,        group: "admin" },
  { key: "marketing",     label: "Marketing Kit",  icon: Rocket,          group: "admin" },

  // LEAGUE — previously lived in its own workspace / shell. Merged into
  // Gabruz Universe: role-gated so only committee-tier users see them.
  { key: "league-committee",     label: "Executive Committee", icon: Landmark,     group: "league" },
  { key: "league-teams",         label: "Teams Management",    icon: ShieldCheck,  group: "league" },
  { key: "league-schedule",      label: "Schedule Management", icon: CalendarDays, group: "league" },
  { key: "league-umpires",       label: "Umpiring Pool",       icon: Gavel,        group: "league" },
  { key: "league-finances",      label: "Finances",            icon: Wallet,       group: "league" },
  // League Announcements merged into the club Announcements view via a
  // Club / League scope toggle. No separate nav item.

  // OFFICIATING — for users with the UMPIRE role. Reached via a Home
  // dashboard tile (no sidebar entry). Kept in `nav` so Module.jsx can
  // still route to it and Register / deep-links keep working.
  { key: "umpire-polls",  label: "My Polls",       icon: Gavel,           group: "officiating" },

  // REFERENCE — User Guide / permission matrix. Reached from the
  // topbar overflow menu (⋮) and the profile dropdown; no sidebar
  // entry so it doesn't clutter the daily navigation.
  { key: "user-guide",    label: "User Guide",     icon: BookOpen,        group: "reference" }
];

// Ordered group definitions for the mobile V4 renderer.
export const NAV_GROUPS = [
  { key: "my",           label: "My Stuff" },
  { key: "team",         label: "Team" },
  { key: "club",         label: "Club" },
  { key: "admin",        label: "Admin" },
  { key: "league",       label: "League" },
  { key: "officiating",  label: "Officiating" }
];

// Desktop V3 information architecture — 6 task-first top-level sections
// used by ShellV3.jsx. Each section owns one or more nav items (see the
// `nav` array above). A section is auto-hidden if the current user has
// no access to any of its children, so "Manage" quietly disappears for
// non-admins. Mobile V4 continues to render the flat `nav` array with
// NAV_GROUPS section headers — untouched.
//
//   Home       → dashboard
//   Play       → schedule, squad, practice
//   Community  → members, groups, announcements, messages, media
//   Splits     → splits
//   Stats      → rankings
//   Manage     → teams, finance, admin
//
export const DESKTOP_SECTIONS = [
  { key: "home",      label: "Home",      icon: LayoutDashboard, children: ["dashboard"] },
  { key: "play",      label: "Play",      icon: CalendarDays,    children: ["schedule", "squad", "practice", "tournaments"] },
  { key: "community", label: "Community", icon: Users,           children: ["members", "groups", "announcements", "messages", "media"] },
  { key: "splits",    label: "Splits",    icon: Receipt,         children: ["splits"] },
  { key: "stats",     label: "Stats",     icon: Crown,           children: ["rankings"] },
  { key: "manage",    label: "Manage",    icon: Database,        children: ["teams", "finance", "admin", "marketing"] },
  // League — committee-only. Auto-hidden if the user has no access to
  // any child (drives the "only committee members see League" gate).
  // Announcements are handled inside the club Announcements view via a
  // scope toggle, so there's no league-announcements child here.
  { key: "league",    label: "League",    icon: Landmark,
    children: ["league-committee", "league-teams", "league-schedule", "league-umpires", "league-finances"] }
  // Officiating (umpire-polls) is intentionally NOT a top-level section
  // — umpires reach it via a Home dashboard tile per product decision.
];

export const can = (user, key) =>
  user.roles.some(r => rolePermissions[r]?.includes(key));

// Check whether `user` can see nav item `item` given the loaded team
// list. Combines the role-permission check with the `requiresTeam`
// gate so a user who technically has the role but isn't on the
// required team still can't see the item. `teams` is state.teams.
export function navItemVisible(user, item, teams) {
  if (!user) return false;
  if (item.desktopOnly) return false;
  if (!can(user, item.key)) return false;
  if (item.requiresTeam) {
    const t = (teams || []).find(x => x.code === item.requiresTeam);
    const roster = Array.isArray(t?.players) ? t.players : [];
    if (!roster.includes(user.id)) return false;
  }
  return true;
}
